import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsAPI } from '../../services/api';

function StatusCards() {
  const navigate = useNavigate();
  const [statusGroups, setStatusGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStatusCards();
  }, []);

  const fetchStatusCards = async () => {
    setLoading(true);
    try {
      const response = await leadsAPI.getStatusCards();
      setStatusGroups(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch status cards');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed Won': return 'bg-green-50 border-green-200 text-green-800';
      case 'Closed Lost': return 'bg-red-50 border-red-200 text-red-800';
      case 'Cold Lead': return 'bg-gray-50 border-gray-200 text-gray-800';
      case 'Prospecting': return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'Qualified': return 'bg-emerald-50 border-emerald-200 text-emerald-800';
      case 'Demo Scheduled / Done': return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'Evaluation / Pilot': return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      case 'Decision': return 'bg-orange-50 border-orange-200 text-orange-800';
      default: return 'bg-slate-50 border-slate-200 text-slate-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
              <div>
                <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-64 bg-white/60 rounded-xl border-2 border-gray-200"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-3 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center space-x-3 sm:space-x-4 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-blue-600 bg-clip-text text-transparent">Lead Progress Status</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">View leads organized by their current progress status</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center space-x-2">
            <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {statusGroups.map((group) => (
            <div
              key={group._id}
              className={`rounded-xl border-2 p-4 sm:p-6 hover:shadow-xl hover:scale-105 transition-all duration-200 ${getStatusColor(group._id)}`}
            >
              {/* Status Header */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base sm:text-lg font-bold truncate pr-2">{group._id}</h3>
                  <span className="bg-white/80 text-xs font-medium px-2 py-1 rounded-full flex-shrink-0">
                    {group.count}
                  </span>
                </div>
                <div className="text-xs text-gray-600">
                  <div className="flex items-center space-x-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Last: {formatDate(group.lastUpdate)}</span>
                  </div>
                </div>
              </div>

              {/* Schools List */}
              <div className="space-y-2 sm:space-y-3 max-h-48 sm:max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {group.leads.map((lead) => (
                  <div
                    key={lead._id}
                    className="bg-white/80 backdrop-blur-sm rounded-lg p-3 border border-white/60 hover:bg-white hover:shadow-md cursor-pointer transition-all duration-200 group"
                    onClick={() => navigate('/admin/lead-listing')}
                    title="Click to view in Lead Listing"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-semibold text-sm truncate pr-2 group-hover:text-blue-600 transition-colors" title={lead.schoolName}>
                        {lead.schoolName}
                      </div>
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{formatDate(lead.updatedAt)}</span>
                      </div>
                      <div className="space-y-1">
                        {lead.primaryAssignee && (
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span className="text-blue-600 font-medium text-xs">P:</span>
                            <span className="truncate">{lead.primaryAssignee.username}</span>
                          </div>
                        )}
                        {lead.secondaryAssignees && lead.secondaryAssignees.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-green-600 font-medium text-xs">S:</span>
                            <span className="truncate">{lead.secondaryAssignees.map(a => a.username).join(', ')}</span>
                          </div>
                        )}
                        {!lead.primaryAssignee && (!lead.secondaryAssignees || lead.secondaryAssignees.length === 0) && (
                          <div className="flex items-center space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                            <span className="text-gray-400 italic text-xs">Unassigned</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {group.leads.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-12 h-12 mx-auto mb-3 bg-white/60 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500">No leads in this status</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {statusGroups.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
            <p className="text-gray-500">Start by creating some leads to see them organized by status.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default StatusCards;